import {
  ArrowLeft,
  Bell,
  Eye,
  EyeOff,
  Loader2,
  Palette,
  Save,
  Settings,
  Shield,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useTheme } from "../../contexts/theme-context";
import { useAuth } from "../../hooks/use-auth";
import { useNotification } from "../../hooks/useNotification";
import { useAppDispatch } from "../../redux/hooks";
import { changePassword, updateProfile } from "../../redux/slices/authSlice.js";
import { notificationHelpers } from "../../utils/notificationHelpers";

export function ProfileContent() {
  const { notify } = useNotification();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [notificationError, setNotificationError] = useState("");
  const [preferenceError, setPreferenceError] = useState("");

  // Settings state
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [auditReminders, setAuditReminders] = useState(true);
  const [reportNotifications, setReportNotifications] = useState(true);

  const [ProfileTheme, setProfileTheme] = useState<"light" | "dark" | "system">(
    "system",
  );
  const { setTheme } = useTheme();

  const userId = user?._id || user?.id;
  const savedName = user?.name ?? "";
  const savedEmail = user?.email ?? "";
  const savedEmailNotifications =
    user?.notificationPreferences?.emailNotifications ?? true;
  const savedPushNotifications =
    user?.notificationPreferences?.pushNotifications ?? false;
  const savedAuditReminders =
    user?.notificationPreferences?.auditReminders ?? true;
  const savedReportNotifications =
    user?.notificationPreferences?.reportNotifications ?? true;
  const savedTheme = user?.applicationPreferences?.theme ?? "system";

  // Hydrate each tab only when its own saved values change. Saving another
  // tab must not discard this tab's pending edits when Redux replaces user.
  useEffect(() => {
    setName(savedName);
    setEmail(savedEmail);
  }, [userId, savedName, savedEmail]);
  useEffect(() => {
    setEmailNotifications(savedEmailNotifications);
    setPushNotifications(savedPushNotifications);
    setAuditReminders(savedAuditReminders);
    setReportNotifications(savedReportNotifications);
  }, [
    userId,
    savedEmailNotifications,
    savedPushNotifications,
    savedAuditReminders,
    savedReportNotifications,
  ]);
  useEffect(() => {
    setProfileTheme(savedTheme);
  }, [userId, savedTheme]);
  useEffect(() => {
    if (!isLoading && !user) navigate("/");
  }, [user, isLoading, navigate]);

  const profileChanged = name !== savedName;
  const notificationsChanged =
    emailNotifications !== savedEmailNotifications ||
    pushNotifications !== savedPushNotifications ||
    auditReminders !== savedAuditReminders ||
    reportNotifications !== savedReportNotifications;
  const preferencesChanged = ProfileTheme !== savedTheme;
  const passwordChanged = Boolean(
    currentPassword || newPassword || confirmPassword,
  );

  // if (isLoading) {
  //   return (
  //     <div className="min-h-screen bg-slate-50 dark:bg-surface flex items-center justify-center">
  //       <div className="text-center">
  //         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
  //         <p className="text-slate-600 dark:text-muted-foreground">Loading...</p>
  //       </div>
  //     </div>
  //   )
  // }

  if (!user) {
    return null;
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      case "manager":
        return "bg-blue-100 text-blue-800 dark:bg-brand/30 dark:text-link";
      case "auditor":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-surface/30 dark:text-muted-foreground";
    }
  };

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;
    if (error && typeof error === "object") {
      const apiError = error as { message?: unknown; error?: unknown };
      if (typeof apiError.message === "string") return apiError.message;
      if (typeof apiError.error === "string") return apiError.error;
    }
    return fallback;
  };

  const handleSaveProfile = async () => {
    if (!profileChanged || isSavingProfile) return;
    setIsSavingProfile(true);
    setProfileError("");
    try {
      const saved = await dispatch(updateProfile({ name })).unwrap();
      setName((current) =>
        current === name ? (saved.user?.name ?? name) : current,
      );
      const config = notificationHelpers.formSaveSuccess("Profile");
      notify(config.message, config.type, config.options);
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to save profile");
      setProfileError(errorMessage);
      notify(errorMessage, "error", { duration: 5000 });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordChanged || isChangingPassword) return;
    setIsChangingPassword(true);
    setPasswordError("");
    try {
      await dispatch(
        changePassword({ currentPassword, newPassword, confirmPassword }),
      ).unwrap();
      const config = notificationHelpers.formSaveSuccess("Password Changed");
      notify(config.message, config.type, config.options);
      // Clear password fields on success
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      const errorMessage = getErrorMessage(error, "Failed to change password");
      setPasswordError(errorMessage);
      notify(errorMessage, "error", { duration: 5000 });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSaveNotification = async () => {
    if (!notificationsChanged || isSavingNotifications) return;
    setIsSavingNotifications(true);
    setNotificationError("");
    try {
      await dispatch(
        updateProfile({
          notificationPreferences: {
            emailNotifications: emailNotifications,
            pushNotifications: pushNotifications,
            auditReminders: auditReminders,
            reportNotifications: reportNotifications,
          },
        }),
      ).unwrap();
      const config = notificationHelpers.formSaveSuccess(
        "Notification Preferences",
      );
      notify(config.message, config.type, config.options);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Failed to save notification preferences",
      );
      setNotificationError(errorMessage);
      notify(errorMessage, "error", { duration: 5000 });
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const handleSavePreference = async () => {
    if (!preferencesChanged || isSavingPreferences) return;
    setIsSavingPreferences(true);
    setPreferenceError("");
    try {
      await dispatch(
        updateProfile({
          applicationPreferences: {
            theme: ProfileTheme,
          },
        }),
      ).unwrap();
      setTheme(ProfileTheme);
      const config = notificationHelpers.formSaveSuccess(
        "Application Preferences",
      );
      notify(config.message, config.type, config.options);
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        "Failed to save application preferences",
      );
      setPreferenceError(errorMessage);
      notify(errorMessage, "error", { duration: 5000 });
    } finally {
      setIsSavingPreferences(false);
    }
  };
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-surface">
      {/* Header */}
      <header className="bg-white dark:bg-card border-b border-slate-200 dark:border-border px-4 py-4 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-foreground">
              Account Settings
            </h1>
            <p className="text-sm text-slate-600 dark:text-muted-foreground">
              Manage your profile and preferences
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Account Summary Sidebar */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-center">Account Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 dark:bg-none dark:bg-brand/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="w-10 h-10 text-blue-600 dark:text-link" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-foreground">
                  {user.name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-muted-foreground truncate">
                  {user.email}
                </p>
              </div>

              <Separator />

              <div className="space-y-3">
                {/* <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500 dark:text-muted-foreground">
                    User ID:
                  </span>
                  <span className="font-mono text-slate-900 dark:text-foreground text-xs">
                    {user._id || user.id}
                  </span>
                </div> */}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500 dark:text-muted-foreground">
                    Role:
                  </span>
                  <Badge
                    className={getRoleBadgeColor(user.role)}
                    variant="outline"
                  >
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500 dark:text-muted-foreground">
                    Status:
                  </span>
                  <Badge
                    variant="default"
                    className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  >
                    Active
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500 dark:text-muted-foreground">
                    Member Since:
                  </span>
                  <span className="text-slate-900 dark:text-foreground text-xs">
                    {user?.createdAt?.substr(0, 10)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Settings Tabs */}
          <div className="min-w-0 lg:col-span-3">
            <Tabs defaultValue="profile" className="space-y-6">
              <TabsList
                aria-label="Account settings"
                className="grid h-auto w-full grid-cols-2 gap-1 p-1 sm:grid-cols-4"
              >
                <TabsTrigger
                  value="profile"
                  className="flex h-auto min-h-11 min-w-0 items-center gap-2 px-2 py-2"
                >
                  <User className="w-4 h-4" />
                  Profile
                </TabsTrigger>
                <TabsTrigger
                  value="security"
                  className="flex h-auto min-h-11 min-w-0 items-center gap-2 px-2 py-2"
                >
                  <Shield className="w-4 h-4" />
                  Security
                </TabsTrigger>
                <TabsTrigger
                  value="notifications"
                  className="flex h-auto min-h-11 min-w-0 items-center gap-2 px-2 py-2"
                >
                  <Bell className="w-4 h-4" />
                  Notifications
                </TabsTrigger>
                <TabsTrigger
                  value="preferences"
                  className="flex h-auto min-h-11 min-w-0 items-center gap-2 px-2 py-2"
                >
                  <Settings className="w-4 h-4" />
                  Preferences
                </TabsTrigger>
              </TabsList>

              {/* Profile Tab */}
              <TabsContent value="profile">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Profile Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          readOnly={true}
                          id="email"
                          type="email"
                          value={email}
                          // onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Role</Label>
                      <div>
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {user.role.charAt(0).toUpperCase() +
                            user.role.slice(1)}
                        </Badge>
                        <p className="text-sm text-slate-500 dark:text-muted-foreground mt-1">
                          Contact your administrator to change your role
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={handleSaveProfile}
                        disabled={isSavingProfile || !profileChanged}
                        className="flex items-center gap-2"
                      >
                        {isSavingProfile ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        {isSavingProfile ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate("/dashboard")}
                      >
                        Cancel
                      </Button>
                    </div>
                    {profileError && (
                      <p
                        role="alert"
                        className="text-sm text-red-600 dark:text-red-400"
                      >
                        {profileError}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Security Tab */}
              <TabsContent value="security">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Security Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-slate-900 dark:text-foreground">
                        Change Password
                      </h3>

                      <div className="space-y-2">
                        <Label htmlFor="currentPassword">
                          Current Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="currentPassword"
                            type={showPassword ? "text" : "password"}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter current password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="newPassword">New Password</Label>
                          <Input
                            id="newPassword"
                            type={showPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="confirmPassword">
                            Confirm Password
                          </Label>
                          <Input
                            id="confirmPassword"
                            type={showPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                          />
                        </div>
                      </div>

                      <Button
                        onClick={handleChangePassword}
                        disabled={isChangingPassword || !passwordChanged}
                        className="flex items-center gap-2"
                      >
                        {isChangingPassword ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Shield className="w-4 h-4" />
                        )}
                        {isChangingPassword ? "Updating..." : "Update Password"}
                      </Button>
                      {passwordError && (
                        <p
                          role="alert"
                          className="text-sm text-red-600 dark:text-red-400"
                        >
                          {passwordError}
                        </p>
                      )}
                    </div>

                    {/* <Separator />

                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-slate-900 dark:text-foreground">
                        Account Actions
                      </h3>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          className="flex items-center gap-2 bg-transparent"
                        >
                          <Download className="w-4 h-4" />
                          Export Data
                        </Button>
                        <Button
                          variant="destructive"
                          className="flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Account
                        </Button>
                      </div>
                    </div> */}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Notifications Tab */}
              <TabsContent value="notifications">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5" />
                      Notification Preferences
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="emailNotifications">
                            Email Notifications
                          </Label>
                          <p className="text-sm text-slate-500 dark:text-muted-foreground">
                            Receive notifications via email
                          </p>
                        </div>
                        <Switch
                          id="emailNotifications"
                          checked={emailNotifications}
                          onCheckedChange={setEmailNotifications}
                        />
                      </div>

                      {/* <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="pushNotifications">
                            Push Notifications
                          </Label>
                          <p className="text-sm text-slate-500 dark:text-muted-foreground">
                            Receive browser push notifications
                          </p>
                        </div>
                        <Switch
                          id="pushNotifications"
                          checked={pushNotifications}
                          onCheckedChange={setPushNotifications}
                        />
                      </div> */}

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="auditReminders">
                            Audit Reminders
                          </Label>
                          <p className="text-sm text-slate-500 dark:text-muted-foreground">
                            Get reminded about pending audits
                          </p>
                        </div>
                        <Switch
                          id="auditReminders"
                          checked={auditReminders}
                          onCheckedChange={setAuditReminders}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label htmlFor="reportNotifications">
                            Report Notifications
                          </Label>
                          <p className="text-sm text-slate-500 dark:text-muted-foreground">
                            Notifications when reports are ready
                          </p>
                        </div>
                        <Switch
                          id="reportNotifications"
                          checked={reportNotifications}
                          onCheckedChange={setReportNotifications}
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleSaveNotification}
                      disabled={isSavingNotifications || !notificationsChanged}
                      className="flex items-center gap-2"
                    >
                      {isSavingNotifications ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {isSavingNotifications
                        ? "Saving..."
                        : "Save Notification Settings"}
                    </Button>
                    {notificationError && (
                      <p
                        role="alert"
                        className="text-sm text-red-600 dark:text-red-400"
                      >
                        {notificationError}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Preferences Tab */}
              <TabsContent value="preferences">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Application Preferences
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <div>
                        <Label className="flex items-center gap-2 mb-3">
                          <Palette className="w-4 h-4" />
                          Theme Preference
                        </Label>
                        <p className="text-sm text-slate-500 dark:text-muted-foreground mb-3">
                          Save your default theme for your account. The header
                          theme control changes the appearance of this app only.
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                          <Button
                            onClick={() => setProfileTheme("light")}
                            variant="outline"
                            className={`h-auto p-4 flex flex-col gap-2 ${ProfileTheme === "light" ? "border-blue-500 bg-blue-50 text-blue-900 dark:border-primary-border dark:bg-brand/40 dark:text-foreground" : "bg-transparent"}`}
                          >
                            <div className="w-8 h-8 bg-white border-2 border-slate-200 rounded"></div>
                            <span className="text-sm">Light</span>
                          </Button>
                          <Button
                            onClick={() => setProfileTheme("dark")}
                            variant="outline"
                            className={`h-auto p-4 flex flex-col gap-2 ${ProfileTheme === "dark" ? "border-blue-500 bg-blue-50 text-blue-900 dark:border-primary-border dark:bg-brand/40 dark:text-foreground" : "bg-transparent"}`}
                          >
                            <div className="w-8 h-8 bg-slate-800 border-2 border-slate-600 rounded"></div>
                            <span className="text-sm">Dark</span>
                          </Button>
                          <Button
                            onClick={() => setProfileTheme("system")}
                            variant="outline"
                            className={`h-auto p-4 flex flex-col gap-2 ${ProfileTheme === "system" ? "border-blue-500 bg-blue-50 text-blue-900 dark:border-primary-border dark:bg-brand/40 dark:text-foreground" : "bg-transparent"}`}
                          >
                            <div className="w-8 h-8 bg-gradient-to-br from-white to-slate-800 border-2 border-slate-400 rounded"></div>
                            <span className="text-sm">System</span>
                          </Button>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={handleSavePreference}
                      disabled={isSavingPreferences || !preferencesChanged}
                      className="flex items-center gap-2"
                    >
                      {isSavingPreferences ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {isSavingPreferences ? "Saving..." : "Save Preferences"}
                    </Button>
                    {preferenceError && (
                      <p
                        role="alert"
                        className="text-sm text-red-600 dark:text-red-400"
                      >
                        {preferenceError}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
}
